import { styled } from "@mui/system";
import { Button, TextField } from "@mui/material";

/**
 * Fondo con imagen (jpeg) + overlay (mismo look del login)
 */
export const RegistroBackground = styled("div", {
    shouldForwardProp: (prop) => prop !== "bg",
})(({ bg }) => ({
    height: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#0B0F17",
    backgroundImage: `url(${bg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
}));

export const BackgroundOverlay = styled("div")({
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background:
        "radial-gradient(1200px 800px at 20% 20%, rgba(0,0,0,0.30), rgba(0,0,0,0.72))",
    backdropFilter: "saturate(120%)",
});

/**
 * Card glass (contenedor principal)
 */
export const RegistroContainer = styled("div")({
    width: "100%",
    maxWidth: 980,
    minHeight: 560,
    display: "flex",
    borderRadius: 18,
    overflow: "hidden",
    position: "relative",
    zIndex: 2,

    background: "rgba(255, 255, 255, 0.10)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    boxShadow: "0 18px 60px rgba(0, 0, 0, 0.55)",
    backdropFilter: "blur(14px)",

    "@media (max-width: 900px)": {
        maxWidth: 560,
        minHeight: "auto",
        flexDirection: "column",
    },
});

/**
 * Panel izquierdo (branding)
 * Mantengo el nombre RegistroTextContainer por compatibilidad con tu estructura original,
 * pero visualmente es el "LeftContainer" del estilo del login.
 */
export const RegistroTextContainer = styled("div")({
    width: "45%",
    padding: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 14,
    position: "relative",

    background:
        "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
    borderRight: "1px solid rgba(255, 255, 255, 0.12)",

    "@media (max-width: 900px)": {
        width: "100%",
        padding: 26,
        borderRight: "none",
        borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
    },
});

/**
 * Panel derecho (form)
 * Mantengo el nombre RegistroFormContainer por compatibilidad.
 */
export const RegistroFormContainer = styled("div")({
    width: "55%",
    padding: 42,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

    "@media (max-width: 900px)": {
        width: "100%",
        padding: 26,
    },
});

export const Logo = styled("img")({
    width: "78%",
    maxWidth: 280,
    height: "auto",
    filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.35))",
    userSelect: "none",
});

export const BrandHint = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 12.5,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    opacity: 0.85,
    color: "rgba(255,255,255,0.85)",
});

/**
 * Header del formulario
 */
export const FormHeader = styled("div")({
    width: "100%",
    color: "rgba(255,255,255,0.92)",
});

/**
 * Inputs minimalistas (igual al login)
 */
export const StyledTextField = styled(TextField)({
    width: "100%",

    "& .MuiInputLabel-root": {
        color: "rgba(255,255,255,0.72)",
    },

    "& .MuiInputBase-root": {
        color: "rgba(255,255,255,0.92)",
        background: "rgba(255,255,255,0.06)",
        borderRadius: 12,
        transition:
            "transform 160ms ease, background 160ms ease, border-color 160ms ease",
    },

    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255,255,255,0.16)",
    },

    "& .MuiInputBase-root:hover": {
        background: "rgba(255,255,255,0.08)",
    },

    "& .MuiInputBase-root.Mui-focused": {
        background: "rgba(255,255,255,0.10)",
    },

    "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255,255,255,0.40)",
    },

    "& .MuiFormHelperText-root": {
        color: "rgba(255,255,255,0.60)",
        marginLeft: 2,
    },
});

/**
 * Botones (misma estética del login)
 */
export const PrimaryButton = styled(Button)({
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 900,
    letterSpacing: 0.2,
    textTransform: "none",
    boxShadow: "none",

    backgroundColor: "var(--primary-color)",
    color: "#fff",

    "&:hover": {
        backgroundColor: "var(--primary-color-dark)",
        boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
        transform: "translateY(-1px)",
    },

    "&:active": {
        transform: "translateY(0px)",
    },

    "&.Mui-disabled": {
        opacity: 0.7,
        color: "rgba(255,255,255,0.85)",
    },
});

export const SecondaryButton = styled(Button)({
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 900,
    letterSpacing: 0.2,
    textTransform: "none",

    color: "rgba(255,255,255,0.92)",
    borderColor: "rgba(255,255,255,0.28)",
    background: "rgba(255,255,255,0.04)",

    "&:hover": {
        borderColor: "rgba(255,255,255,0.45)",
        background: "rgba(255,255,255,0.06)",
        transform: "translateY(-1px)",
    },

    "&:active": {
        transform: "translateY(0px)",
    },

    "&.Mui-disabled": {
        opacity: 0.7,
    },
});

export const ErrorText = styled("div")({
    width: "100%",
    marginBottom: 12,
    padding: "10px 12px",
    borderRadius: 12,
    background: "rgba(255, 90, 90, 0.12)",
    border: "1px solid rgba(255, 90, 90, 0.22)",
    color: "rgba(255,255,255,0.92)",
    fontSize: 14,
});

/**
 * Modal content (para que se vea igual de “premium”)
 * Ojo: No depende de cómo esté construido tu <Modal />, solo estiliza el contenido interno.
 */
export const ModalContentWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: 6,
    textAlign: "center",
});

export const ModalActionsRow = styled("div")({
    width: "100%",
    display: "flex",
    gap: 10,
    marginTop: 14,

    "@media (max-width: 520px)": {
        flexDirection: "column",
    },
});
